require('./polyfill')
var errorTypes = require('./constants/error-types')
var utils = require('./utils')
var validateEmail = require('./utils/validate-email')
var phoneUtils = require('./utils/phone').utils
var meridianPhoneUtils = require('./utils/meridian-phone').utils
var formatDynamics = require('./utils/format-dynamics').format
var translationStore = require('./stores/translation-store')
var localeStore = require('./stores/create-locale-store')(
  typeof window !== 'undefined' && window.__LOCALE_DATA__
)

/**
 * @param {str} key The english translation string. Will check a local translation store
 * for a match, defaulting to the english key if no match is found.
 * @param {object|array} data Data to make available to dynamic phrases in a translation string.
 * @param {object} [options] Optional options, such as namespace.
 * @param {str} [options.namespace] String value of a namespace for key lookup.
 * @returns {string} The translated string with any dynamic phrases replaced by type.
 */
function translate(key, data, options) {
  if (typeof key !== 'string') return ''
  var translation = translationStore.getKey(key, options)
  return formatDynamics(translation, data, localeStore.getState())
}

/**
 * A proxy to make global Locale object available
 * when calling utils methods directly rather than via __().
 * @param {function} fn The function to proxy.
 * @returns {function} A function wrapping fn that will add locale info as the last argument.
 */
function injectLocals(fn) {
  return function () {
    var args = [].slice.call(arguments)
    // make sure locals always get injected as last argument
    var missing = fn.length - 1 - args.length
    while (missing > 0) {
      args.push(undefined)
      missing--
    }
    return fn.apply(null, args.concat(localeStore.getState()))
  }
}

// get the utils that should be exported for one-off use
var exportedUtils = {
  addTranslations: translationStore.add.bind(translationStore),
  clearTranslations: translationStore.clear.bind(translationStore),
  errorTypes: errorTypes,
  removeKey: translationStore.removeKey.bind(translationStore),
  removeNamespace: translationStore.removeNamespace.bind(translationStore),
  getPhoneFormat: () => phoneUtils.format,
  getMeridianPhoneFormat: () => meridianPhoneUtils.format,
  isValidCountryCode: phoneUtils.isValidCountryCode,
  getCountryCode: function () {
    return localeStore.get('countryCode')
  },
  getCurrency: function () {
    return localeStore.get('currency')
  },
  getCurrencySymbol: function () {
    return localeStore.get('currencySymbol')
  },
  getFullCurrencySymbol: function (locale, currency) {
    if (locale === 'es-US') {
      locale = 'en-US'
    }

    var number = 0
    return number
      .toLocaleString(locale || 'en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/\d/g, '')
      .trim()
  },
  getLocale: function () {
    return localeStore.get('locale')
  },
  getLocaleData: function () {
    return localeStore.getState()
  },
  getTimezone: function () {
    return localeStore.get('timezone')
  },
  is12Hr: function () {
    return localeStore.get('is12Hr')
  },
  is24Hr: function () {
    return localeStore.get('is24Hr')
  },
  setCurrency: localeStore.setCurrency.bind(localeStore),
  setLocale: localeStore.setLocale.bind(localeStore),
  setTimezone: localeStore.setTimezone.bind(localeStore),
  set12Hr: localeStore.set12Hr.bind(localeStore),
  set24Hr: localeStore.set24Hr.bind(localeStore),
  syncTranslations: translationStore.syncTranslations.bind(translationStore),
  unwatch: localeStore.unwatch.bind(localeStore),
  validateEmail: validateEmail,
  watch: localeStore.watch.bind(localeStore),
}
// all imported utils need to be proxied by injectLocals
// so they still have access to the global locale and timezone
for (var key in utils) {
  exportedUtils[key] = injectLocals(utils[key])
}

exports.__ = translate
exports.i18nUtils = exportedUtils
